/* eslint-disable react-refresh/only-export-components */
import type { VariantProps } from "class-variance-authority";

import { cva } from "class-variance-authority";
import * as React from "react";

import { useUIStateStore } from "../../store/uiStateStore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../tooltip/Tooltip";
import { cn } from "../utils/utils";

type ButtonProps = {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: boolean;
  tooltipText?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

const buttonVariants = cva(
  "group inline-flex items-center w-auto font-sentric-alt whitespace-wrap rounded-lg text-sm font-medium ring-offset-background transition ease-in-out duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-25 uppercase py-2 px-5",
  {
    variants: {
      variant: {
        primary:
          "justify-between bg-white border-[1px] border-sentric-dark-background text-black  hover:bg-primary-button-hover active:bg-black active:text-sentric-light-grey ",
        darkBackgroundPrimary:
          "justify-between bg-transparent text-sentric-light-grey -[1px] border-white hover:bg-primary-dark-button-hover active:bg-white active:text-black",
        secondary:
          "justify-center bg-sentric-orange hover:bg-sentric-orange-hover active:bg-sentric-orange-active",
        whiteSecondary:
          "justify-center bg-white  hover:bg-sentric-light-grey active:bg-secondary-white-button-active",
        darkSecondary:
          "justify-center bg-sentric-dark-background  text-sentric-light-grey hover:bg-dark-secondary-hover active:bg-dark-secondary-active",
        link: "text-sentric-dark-background  hover:text-sentric-orange active:text-sentric-orange p-0 stroke-sentric-dark-background  hover:stroke-sentric-orange active:stroke-sentric-orange  disabled:text-sentric-light-grey",
        tunecoreLink: "group text-white hover:text-tunecore-green active:text-tunecore-green p-0 stroke-white hover:stroke-tunecore-green active:stroke-tunecore-green disabled:text-gray-400",
        outline:
          "border-[1px] border-sentric-dark-background  hover:border-sentric-orange",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        shiny: "",
        tunecorePrimary:
          "px-5 py-2.5 text-white font-bold rounded-full uppercase tracking-wider ",
        tunecoreSecondary:
          "justify-center bg-transparent px-5 py-2.5 ring-[2px] ring-white text-white rounded-full hover:ring-tunecore-green active:ring-tunecore-green font-bold text-[13px] leading-[100%] tracking-[1px] uppercase ",
        tunecoreError:
          "px-3 py-5 text-white font-bold rounded-full uppercase tracking-wider bg-tunecore-error-red",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      tooltipText,
      tooltip,
      label,
      icon,
      variant = "primary",
      disabled,
      ...props
    },
    ref,
  ) => {
    const isTunecore = useUIStateStore(state => state.isTunecore);

    // Auto-switch primary to tunecorePrimary and secondary to tunecoreSecondary for TuneCore
    const effectiveVariant = isTunecore
      ? variant === "primary"
        ? "tunecorePrimary"
        : variant === "secondary"
          ? "tunecoreSecondary"
          : variant === "link"
            ? "tunecoreLink"
            : variant
      : variant;

    const buttonClass = cn(buttonVariants({ variant: effectiveVariant }), className);

    // TuneCore variants need special gradient styling
    if (effectiveVariant === "tunecorePrimary" || effectiveVariant === "tunecoreError") {
      const gradientStyle = effectiveVariant === "tunecoreError"
        ? undefined
        : {
            background: disabled
              ? "linear-gradient(to bottom right, rgba(0, 239, 134, 0.5), rgba(0, 142, 185, 0.5))"
              : "linear-gradient(to bottom right, #00ef86, #008eb9)",
            borderRadius: "50px",
            padding: "12px 20px",
          };

      if (tooltip) {
        return (
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger
                className={buttonClass}
                style={gradientStyle}
                disabled={disabled}
                {...props}
              >
                <div className="flex items-center">
                  {icon != null && (
                    <span className="mr-2">{icon}</span>
                  )}
                  <p>{label}</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>{tooltipText}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      else {
        return (
          <button
            ref={ref}
            type="button"
            className={buttonClass}
            style={gradientStyle}
            disabled={disabled}
            {...props}
          >
            <div className="flex items-center">
              <p>{label}</p>
              {icon != null && (
                <span className="ml-3">{icon}</span>
              )}
            </div>
          </button>
        );
      }
    }

    if (variant === "shiny") {
      return (
        <div className="relative inline-flex group">
          <div className="absolute transition-all duration-1000 opacity-90 -inset-px bg-gradient-to-r from-red-700 via-sentric-orange to-sentric-teal rounded-xl blur-lg group-hover:opacity-100 group-hover:-inset-1 group-hover:duration-200 animate-tilt"></div>
          <button
            type="button"
            aria-label={label?.toString()}
            disabled={disabled}
            {...props}
            ref={ref}
            className={cn(
              buttonClass,
              "relative inline-flex items-center justify-center font-bold text-sentric-light-grey transition-all duration-200 bg-sentric-dark-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900",
            )}
          >
            {label}
          </button>
        </div>
      );
    }

    if (tooltip) {
      return (
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger className={buttonClass} disabled={disabled} {...props}>
              {label}
              {icon != null ? (
                <span className="ml-3">{icon}</span>
              ) : null}
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    else {
      return (
        <button ref={ref} type="button" className={buttonClass} disabled={disabled} {...props}>
          {label}
          {icon != null ? (
            <span className="ml-3">{icon}</span>
          ) : null}
        </button>
      );
    }
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
